/**
 * Hupper FPS Arena — Weapon stats (single source of truth).
 *
 * Values referenced from CS2 (Liquipedia + community wiki) and tuned for
 * 1v1 MR12 format. Whenever client and server disagree on numbers, this
 * file wins. Both `apps/fps` (client) and `services/game-server`
 * (authoritative simulator) import from here.
 */

export type WeaponClass = "melee" | "pistol" | "smg" | "rifle" | "sniper" | "grenade";

export type RecoilProfile =
  | "knife"
  | "low"        // pistols
  | "deagle"     // hand cannon — CS:GO-style violent vertical kick + slow recovery
  | "heavy"      // AWP
  | "smg"
  | "rifle-ak"
  | "rifle-m4"
  | "burst";

/** Which CS:GO side can purchase a weapon. Knife & most grenades are "both". */
export type WeaponSide = "T" | "CT" | "both";

/**
 * Spray pattern entry — cumulative view offset from initial aim after
 * firing this bullet of a sustained burst. Spec convention: positive
 * pitch = view kicks UP, positive yaw = view kicks RIGHT (screen-space).
 * The HitDetector / FirstPersonController negate yaw at use site to
 * match PlayCanvas yaw convention (positive = counterclockwise / left).
 */
export type SprayPatternEntry = readonly [pitch: number, yaw: number];

export interface WeaponSpec {
  /** Stable ID used in network messages and contract events. */
  id: string;
  /** Display name shown in HUD/buy menu. */
  name: string;
  /** Class for buy-menu grouping and animations. */
  class: WeaponClass;
  /** Cost in match currency (NOT USDT — round economy). */
  cost: number;
  /** Base damage at point-blank, chest hitbox, no armor. */
  baseDamage: number;
  /** Headshot multiplier (default 4×). */
  headshotMul: number;
  /** Armor penetration percentage (0-100). */
  armorPen: number;
  /**
   * CS:GO range modifier — damage falloff with distance:
   * `damage × rangeModifier^(distance / RANGE_FALLOFF_REF_M)`.
   * 1.0 = no falloff (melee, grenades). Applied server-side in
   * `damageForHit` using the hit ray's travel distance.
   */
  rangeModifier: number;
  /** Rounds per minute. */
  rpm: number;
  /** Magazine size. */
  magSize: number;
  /** Reserve ammo (extra mags). */
  reserveAmmo: number;
  /** Reload time in seconds. */
  reloadTime: number;
  /** Fully automatic? */
  auto: boolean;
  /** Movement speed multiplier when equipped (1.0 = baseline ~250 u/s). */
  speedMod: number;
  /**
   * CS:GO-accurate base movement speed when equipped, in m/s.
   * Consumed by the movement integration loop in both server and client
   * prediction. See Sprint 1.3 spec in docs/FPS_ARENA_ROADMAP.md.
   */
  mobility: number;
  /** First-shot accuracy when standing still (0-1). */
  firstShotAccuracy: number;
  /** Recoil pattern profile (referenced by client viewmodel + server validation). */
  recoilProfile: RecoilProfile;
  /** CS:GO side restriction. Buy menu + server `buy_weapon` enforce this. */
  side: WeaponSide;
  /**
   * Sprint 3 — deterministic per-bullet recoil pattern (cumulative
   * offsets in degrees from the original aim). `null` = no learnable
   * pattern (pistols, sniper, smg fallback) — falls back to legacy
   * single-shot pitch+yaw kick. Length is the magazine size.
   */
  sprayPattern: ReadonlyArray<SprayPatternEntry> | null;
  /**
   * Sprint 3 — base inaccuracy cone (degrees) when the player is
   * standing still with no spray accumulation. Higher = more bullet
   * spread per shot. Pistols 0.3°, rifles 0.4°, AWP 0.05°.
   */
  inaccuracyStanding: number;
  /**
   * Sprint 3 — how much movement adds to inaccuracy. The contribution
   * is `speedFraction * mobilityFactor * 3°` where `speedFraction` is
   * 1.0 at full run, ~0.52 walking, ~0.34 crouching. Sniper 1.5 (worst),
   * rifle 0.8, smg 0.6, pistol 0.4.
   */
  mobilityFactor: number;
  /**
   * Sprint 3 — when true, helmet armor does NOT halve a head-zone hit
   * (AWP / AK-47 / Deagle / SSG-08 all bypass helmet). Defining balance
   * choice that makes AK one-tap heads while M4 doesn't.
   */
  headshotHelmetIgnore: boolean;
  /** Short copy for buy menu / tooltip. */
  desc: string;
}

/**
 * Falloff reference distance — CS:GO applies the range modifier once per
 * 500 hammer units. This game's movement maps 1:1 to CS:GO at
 * 1 u = 0.0256 m (knife 250 u/s = 6.40 m/s, rifle 215 u/s = 5.50 m/s),
 * so 500 u = 12.8 m. `damage × rangeModifier^(meters / 12.8)`.
 */
export const RANGE_FALLOFF_REF_M = 12.8;

/* ─────────────────────────────────────────────────────────────────
   SPRAY PATTERNS — cumulative offsets (deg) from initial aim.

   Spec convention: positive yaw = view kicks RIGHT on screen.
   PlayCanvas convention has positive yaw = counterclockwise (LEFT).
   Apply with `pitch += pattern.pitch` and `yaw -= pattern.yaw`.
   ───────────────────────────────────────────────────────────────── */

/**
 * AK-47 30-bullet spray — iconic vertical-rise → left peek → right snap →
 * jagged tail shape. Every entry is a *unique* cumulative offset from the
 * initial aim, with small continuous deltas between adjacent bullets, so
 * the visual recoil reads as a smooth pull rather than the previous
 * pattern's stepped plateaus (4–6 identical rows in a row) that snapped
 * the camera sideways every cluster boundary and felt robotic.
 *
 * Reference shape (CS:GO ish): peak ~8° vertical, ~-2.1° left, ~+2.7° right.
 */
const AK47_SPRAY: ReadonlyArray<SprayPatternEntry> = [
  [0.0,  0.0],   // 1 — pinpoint
  [1.6,  0.1],   // 2
  [3.0,  0.2],   // 3
  [4.3,  0.3],   // 4
  [5.4,  0.3],   // 5
  [6.2,  0.2],   // 6
  [6.9,  0.0],   // 7  — start drifting left
  [7.4, -0.3],   // 8
  [7.7, -0.7],   // 9
  [7.9, -1.2],   // 10
  [8.0, -1.6],   // 11
  [8.1, -2.0],   // 12 — peak left
  [8.1, -2.1],   // 13
  [8.1, -1.9],   // 14 — reversal
  [8.1, -1.4],   // 15
  [8.1, -0.6],   // 16
  [8.1,  0.4],   // 17 — crossing center
  [8.0,  1.3],   // 18
  [8.0,  2.1],   // 19
  [7.9,  2.6],   // 20
  [7.8,  2.7],   // 21 — peak right
  [7.6,  2.4],   // 22
  [7.3,  1.8],   // 23
  [7.0,  1.0],   // 24
  [6.7,  0.2],   // 25 — falling back
  [6.4, -0.5],   // 26
  [6.2, -1.0],   // 27
  [6.1, -1.3],   // 28
  [6.0, -1.4],   // 29
  [5.9, -1.3],   // 30
] as const;

/**
 * M4A4 30-bullet spray — smoother, smaller vertical kick than AK, with a
 * mild right-drift settling into a left peek then a right swing. Same
 * smoothness rule as the AK pattern: every adjacent pair differs in at
 * least one component so the camera moves on every shot — no plateaus.
 */
const M4A4_SPRAY: ReadonlyArray<SprayPatternEntry> = [
  [0.0,  0.0],   // 1
  [1.4,  0.1],   // 2
  [2.6,  0.2],   // 3
  [3.7,  0.3],   // 4
  [4.5,  0.4],   // 5
  [5.1,  0.4],   // 6
  [5.6,  0.3],   // 7
  [6.0,  0.1],   // 8 — start drifting left
  [6.3, -0.1],   // 9
  [6.5, -0.3],   // 10
  [6.6, -0.5],   // 11
  [6.7, -0.7],   // 12
  [6.7, -0.8],   // 13 — peak left
  [6.7, -0.7],   // 14 — horizontal sweep at the vertical peak
  [6.7, -0.5],   // 15
  [6.7, -0.2],   // 16
  [6.7,  0.2],   // 17 — crossing center
  [6.7,  0.5],   // 18
  [6.6,  0.8],   // 19
  [6.6,  1.0],   // 20
  [6.5,  1.1],   // 21 — peak right
  [6.5,  1.0],   // 22
  [6.4,  0.8],   // 23 — falling back
  [6.3,  0.5],   // 24
  [6.2,  0.2],   // 25
  [6.1, -0.1],   // 26
  [6.0, -0.3],   // 27
  [5.9, -0.4],   // 28
  [5.8, -0.5],   // 29
  [5.7, -0.4],   // 30
] as const;

/**
 * M4A1-S 20-bullet spray — the tightest, most controllable pattern in the
 * arsenal, tuned to feel like the CS:GO suppressed M4. The first ~9 bullets
 * climb almost straight up at an easing rate, then the vertical plateaus near
 * its peak while a gentle horizontal S-wave (right → left → right) plays out.
 *
 * Key control trait: cumulative vertical is monotonic non-decreasing — the
 * view never drifts back DOWN mid-spray, so one steady pull-down compensates
 * the whole mag (the previous pattern dipped 4.5 → 4.0 and felt erratic).
 * Horizontal stays within ±0.4°. Every adjacent pair differs in at least one
 * component, so the camera moves on every shot without identical robotic rows.
 */
const M4A1_S_SPRAY: ReadonlyArray<SprayPatternEntry> = [
  [0.0,  0.0],   // 1 — first shot pinpoint
  [0.8,  0.0],   // 2 — near-vertical climb
  [1.5,  0.1],   // 3
  [2.1,  0.1],   // 4
  [2.6,  0.2],   // 5
  [3.0,  0.2],   // 6 — climb easing
  [3.4,  0.3],   // 7
  [3.6,  0.3],   // 8
  [3.8,  0.4],   // 9
  [3.9,  0.4],   // 10 — vertical tops out, plateau begins
  [4.0,  0.3],   // 11 — gentle wave back toward center
  [4.0,  0.1],   // 12
  [4.1, -0.1],   // 13 — crossing to the left
  [4.1, -0.3],   // 14
  [4.2, -0.4],   // 15 — peak left
  [4.2, -0.3],   // 16 — easing back right
  [4.2, -0.1],   // 17
  [4.3,  0.1],   // 18
  [4.3,  0.3],   // 19
  [4.3,  0.4],   // 20 — settle right
] as const;

/* ─────────────────────────────────────────────────────────────────
   ARSENAL
   ───────────────────────────────────────────────────────────────── */
export const WEAPONS: WeaponSpec[] = [
  // ── MELEE ─────────────────────────────────────────────────────
  {
    id: "knife", name: "Combat Knife", class: "melee",
    cost: 0, baseDamage: 40, headshotMul: 4, armorPen: 85, rangeModifier: 1.0,
    rpm: 0, magSize: 0, reserveAmmo: 0, reloadTime: 0, auto: false,
    speedMod: 1.10, mobility: 6.40,
    firstShotAccuracy: 1.0, recoilProfile: "knife", side: "both",
    sprayPattern: null, inaccuracyStanding: 0, mobilityFactor: 0, headshotHelmetIgnore: true,
    desc: "Slash 40 (90 back), stab 65 (180 back). Always equipped.",
  },

  // ── PISTOLS ───────────────────────────────────────────────────
  {
    id: "g18", name: "G-18", class: "pistol",
    cost: 200, baseDamage: 30, headshotMul: 4, armorPen: 47, rangeModifier: 0.75,
    rpm: 400, magSize: 20, reserveAmmo: 120, reloadTime: 2.2, auto: false,
    speedMod: 1.0, mobility: 6.10,
    firstShotAccuracy: 0.95, recoilProfile: "low", side: "T",
    sprayPattern: null, inaccuracyStanding: 0.30, mobilityFactor: 0.4, headshotHelmetIgnore: false,
    desc: "T-side starter sidearm. Burst-capable.",
  },
  {
    id: "usp_s", name: "USP-S", class: "pistol",
    cost: 200, baseDamage: 35, headshotMul: 4, armorPen: 50.5, rangeModifier: 0.79,
    rpm: 352, magSize: 12, reserveAmmo: 24, reloadTime: 2.2, auto: false,
    speedMod: 1.0, mobility: 6.10,
    firstShotAccuracy: 0.96, recoilProfile: "low", side: "CT",
    sprayPattern: null, inaccuracyStanding: 0.30, mobilityFactor: 0.4, headshotHelmetIgnore: false,
    desc: "CT-side starter. Suppressed, higher damage.",
  },
  {
    id: "deagle", name: "Desert Eagle", class: "pistol",
    // CS:GO-accurate cycle: ~0.3 s between shots. Spamming is heavily
    // punished by recoil reset > cycle — wait for the kick to settle or
    // you'll miss the next shot.
    cost: 700, baseDamage: 53, headshotMul: 4, armorPen: 93.2, rangeModifier: 0.81,
    rpm: 200, magSize: 7, reserveAmmo: 35, reloadTime: 2.2, auto: false,
    speedMod: 0.98, mobility: 5.85,
    firstShotAccuracy: 0.88, recoilProfile: "deagle", side: "both",
    sprayPattern: null, inaccuracyStanding: 0.45, mobilityFactor: 0.4, headshotHelmetIgnore: true,
    desc: "Hand cannon. Slow cycle, brutal kick. One-taps heads.",
  },
  {
    id: "dualies", name: "Dual Berettas", class: "pistol",
    cost: 300, baseDamage: 38, headshotMul: 4, armorPen: 57.5, rangeModifier: 0.75,
    rpm: 650, magSize: 30, reserveAmmo: 120, reloadTime: 4.5, auto: true,
    speedMod: 1.0, mobility: 6.10,
    firstShotAccuracy: 0.80, recoilProfile: "low", side: "both",
    sprayPattern: null, inaccuracyStanding: 0.30, mobilityFactor: 0.4, headshotHelmetIgnore: false,
    desc: "Twin pistols. Spray and pray.",
  },

  // ── SMGs ──────────────────────────────────────────────────────
  {
    id: "mac10", name: "MAC-10", class: "smg",
    cost: 1050, baseDamage: 29, headshotMul: 4, armorPen: 57.5, rangeModifier: 0.82,
    rpm: 800, magSize: 30, reserveAmmo: 100, reloadTime: 2.4, auto: true,
    speedMod: 1.0, mobility: 6.00,
    firstShotAccuracy: 0.80, recoilProfile: "smg", side: "T",
    sprayPattern: null, inaccuracyStanding: 0.50, mobilityFactor: 0.6, headshotHelmetIgnore: false,
    desc: "T-only. Cheap full-auto. Best run-and-gun.",
  },
  {
    id: "mp9", name: "MP9", class: "smg",
    cost: 1250, baseDamage: 26, headshotMul: 4, armorPen: 60, rangeModifier: 0.855,
    rpm: 857, magSize: 30, reserveAmmo: 120, reloadTime: 2.1, auto: true,
    speedMod: 1.0, mobility: 6.00,
    firstShotAccuracy: 0.82, recoilProfile: "smg", side: "CT",
    sprayPattern: null, inaccuracyStanding: 0.50, mobilityFactor: 0.6, headshotHelmetIgnore: false,
    desc: "CT-only. Fast fire rate, controlled spray.",
  },

  // ── RIFLES ────────────────────────────────────────────────────
  {
    id: "ak47", name: "AK-47", class: "rifle",
    cost: 2700, baseDamage: 36, headshotMul: 4, armorPen: 77.5, rangeModifier: 0.98,
    rpm: 600, magSize: 30, reserveAmmo: 90, reloadTime: 2.5, auto: true,
    speedMod: 0.92, mobility: 5.50,
    firstShotAccuracy: 0.92, recoilProfile: "rifle-ak", side: "T",
    sprayPattern: AK47_SPRAY, inaccuracyStanding: 0.55, mobilityFactor: 0.8, headshotHelmetIgnore: true,
    desc: "T-only. High damage. Iconic spray pattern.",
  },
  {
    id: "m4a4", name: "M4A4", class: "rifle",
    cost: 3100, baseDamage: 33, headshotMul: 4, armorPen: 70, rangeModifier: 0.97,
    rpm: 666, magSize: 30, reserveAmmo: 90, reloadTime: 3.1, auto: true,
    speedMod: 0.94, mobility: 5.50,
    firstShotAccuracy: 0.90, recoilProfile: "rifle-m4", side: "CT",
    sprayPattern: M4A4_SPRAY, inaccuracyStanding: 0.40, mobilityFactor: 0.8, headshotHelmetIgnore: false,
    desc: "CT-only. Higher fire rate. Easier control.",
  },
  {
    id: "m4a1_s", name: "M4A1-S", class: "rifle",
    cost: 2900, baseDamage: 38, headshotMul: 4, armorPen: 70, rangeModifier: 0.94,
    rpm: 600, magSize: 20, reserveAmmo: 80, reloadTime: 3.1, auto: true,
    speedMod: 0.94, mobility: 5.50,
    firstShotAccuracy: 0.92, recoilProfile: "rifle-m4", side: "CT",
    sprayPattern: M4A1_S_SPRAY, inaccuracyStanding: 0.40, mobilityFactor: 0.8, headshotHelmetIgnore: false,
    desc: "CT-only. Suppressed. Higher damage, smaller mag.",
  },

  // ── SNIPER ────────────────────────────────────────────────────
  {
    id: "awp", name: "AWP", class: "sniper",
    cost: 4750, baseDamage: 115, headshotMul: 4, armorPen: 97.5, rangeModifier: 0.99,
    rpm: 41, magSize: 5, reserveAmmo: 10, reloadTime: 3.7, auto: false,
    speedMod: 0.84, mobility: 5.10,
    firstShotAccuracy: 1.0, recoilProfile: "heavy", side: "both",
    sprayPattern: null, inaccuracyStanding: 0.05, mobilityFactor: 1.5, headshotHelmetIgnore: true,
    desc: "One-shot kill scoped. 5-round mag.",
  },

  // ── GRENADES ──────────────────────────────────────────────────
  {
    id: "he", name: "HE Grenade", class: "grenade",
    cost: 300, baseDamage: 98, headshotMul: 1, armorPen: 57.5, rangeModifier: 1.0,
    rpm: 0, magSize: 1, reserveAmmo: 0, reloadTime: 0, auto: false,
    speedMod: 1.0, mobility: 6.30,
    firstShotAccuracy: 1.0, recoilProfile: "low", side: "both",
    sprayPattern: null, inaccuracyStanding: 0, mobilityFactor: 0, headshotHelmetIgnore: false,
    desc: "Up to 99 dmg in radius. Falls off with distance.",
  },
  {
    id: "flash", name: "Flashbang", class: "grenade",
    cost: 200, baseDamage: 0, headshotMul: 1, armorPen: 0, rangeModifier: 1.0,
    rpm: 0, magSize: 1, reserveAmmo: 1, reloadTime: 0, auto: false,
    speedMod: 1.0, mobility: 6.30,
    firstShotAccuracy: 1.0, recoilProfile: "low", side: "both",
    sprayPattern: null, inaccuracyStanding: 0, mobilityFactor: 0, headshotHelmetIgnore: false,
    desc: "Blinds enemies in line-of-sight (max 2 carry).",
  },
  {
    id: "smoke", name: "Smoke", class: "grenade",
    cost: 300, baseDamage: 0, headshotMul: 1, armorPen: 0, rangeModifier: 1.0,
    rpm: 0, magSize: 1, reserveAmmo: 0, reloadTime: 0, auto: false,
    speedMod: 1.0, mobility: 6.30,
    firstShotAccuracy: 1.0, recoilProfile: "low", side: "both",
    sprayPattern: null, inaccuracyStanding: 0, mobilityFactor: 0, headshotHelmetIgnore: false,
    desc: "18s vision-blocking smoke cloud.",
  },
  {
    id: "molotov", name: "Molotov", class: "grenade",
    cost: 400, baseDamage: 8, headshotMul: 1, armorPen: 0, rangeModifier: 1.0,
    rpm: 0, magSize: 1, reserveAmmo: 0, reloadTime: 0, auto: false,
    speedMod: 1.0, mobility: 6.30,
    firstShotAccuracy: 1.0, recoilProfile: "low", side: "T",
    sprayPattern: null, inaccuracyStanding: 0, mobilityFactor: 0, headshotHelmetIgnore: false,
    desc: "T-only. 8 dmg/tick × 7s in fire patch.",
  },
  {
    id: "incendiary", name: "Incendiary", class: "grenade",
    cost: 600, baseDamage: 8, headshotMul: 1, armorPen: 0, rangeModifier: 1.0,
    rpm: 0, magSize: 1, reserveAmmo: 0, reloadTime: 0, auto: false,
    speedMod: 1.0, mobility: 6.30,
    firstShotAccuracy: 1.0, recoilProfile: "low", side: "CT",
    sprayPattern: null, inaccuracyStanding: 0, mobilityFactor: 0, headshotHelmetIgnore: false,
    desc: "CT-only fire grenade. 8 dmg/tick × 7s.",
  },
];

/** Lookup by id, throws if unknown. */
export function getWeapon(id: string): WeaponSpec {
  const w = WEAPONS.find((x) => x.id === id);
  if (!w) throw new Error(`Unknown weapon: ${id}`);
  return w;
}

export const WEAPONS_BY_CLASS: Record<WeaponClass, WeaponSpec[]> = {
  melee:   WEAPONS.filter((w) => w.class === "melee"),
  pistol:  WEAPONS.filter((w) => w.class === "pistol"),
  smg:     WEAPONS.filter((w) => w.class === "smg"),
  rifle:   WEAPONS.filter((w) => w.class === "rifle"),
  sniper:  WEAPONS.filter((w) => w.class === "sniper"),
  grenade: WEAPONS.filter((w) => w.class === "grenade"),
};

/**
 * Loadout slot — used by the drop / pickup system. Buying a primary while
 * already holding a primary drops the old one (and same for secondary).
 * Grenades each occupy their own kind-slot so you can hold several types
 * at once. Melee is non-droppable.
 */
export type WeaponSlot = "primary" | "secondary" | "melee" | "grenade";

export function weaponSlot(cls: WeaponClass): WeaponSlot {
  switch (cls) {
    case "rifle":
    case "smg":
    case "sniper":
      return "primary";
    case "pistol":
      return "secondary";
    case "grenade":
      return "grenade";
    case "melee":
    default:
      return "melee";
  }
}

/** Convenience — slot for a given weapon id. */
export function weaponSlotFor(id: string): WeaponSlot {
  const w = WEAPONS.find((x) => x.id === id);
  return w ? weaponSlot(w.class) : "melee";
}

/**
 * Inventory category for a grenade — grenades in the same category share a
 * carry slot, so you can only hold one of them at a time. Molotov (T-side)
 * and Incendiary (CT-side) are the same "fire" slot; every other grenade is
 * its own category. Returns the weapon id for non-grouped grenades so each
 * stays distinct (HE / flash / smoke can be carried alongside each other and
 * alongside a fire grenade).
 */
export function grenadeCategory(id: string): string {
  return id === "molotov" || id === "incendiary" ? "fire" : id;
}
